/** Client-side UI state. The server's PlayerView is the source of truth for the game itself. */
import type { ServerMessage } from '../shared/protocol.ts';
import type { Attribute } from '../engine/rules/attributes.ts';
import { newListState, type ListState } from './picklist.ts';
import type { ActionView, PlayerView, ShopCategory } from '../shared/view.ts';

export interface Targeting {
  action: ActionView;
  strikeKey?: string;
  /** The item to use on the target (a bomb to throw). */
  itemUid?: string;
  /** Acting for your construct companion. */
  actorId?: string;
  /** The option picked from the action's choices (the condition a theory predicts). */
  choice?: string;
  /** Its choices are on the banner, waiting for a pick: map clicks don't target yet. */
  choosing?: boolean;
}

/**
 * Who reads an enchant scroll and with what: a party member (Request; none: you), your powder or theirs,
 * and holy water on the gear first. A bridging step also picks its scroll.
 */
export interface EnchantChoice {
  scrollUid?: string;
  enchanterId?: string;
  powderUid?: string;
  powderFrom?: 'requester' | 'enchanter';
  anoint?: boolean;
}

export interface Store {
  playerId: string | null;
  welcome: Extract<ServerMessage, { t: 'welcome' }> | null;
  joined: boolean;
  view: PlayerView | null;
  targeting: Targeting | null;
  hover: { x: number; y: number } | null;
  /** Click-to-walk destination while exploring. */
  walkTo: { x: number; y: number } | null;
  modalDismissedFor: string | null;
  showLogin: boolean;
  /** The Make Camp dialog is open. */
  campOpen: boolean;
  /**
   * The Give menu (a party member's name clicked, client/transfer.ts): to whom (a character id), what is lined up to
   * send, and the stack whose Transfer chooser is open with what's typed in it.
   */
  transfer: { to: string; lines: { uid: string; qty: number }[]; open: string | null; typed: string } | null;
  /** Your character's journal is open. */
  journalOpen: boolean;
  /** The panel over the map: all your gear (B) or only your consumables (Shift+B). */
  bag: 'all' | 'consumables' | null;
  /**
   * The Enchant dialog (an enchant scroll) or the Anoint dialog (holy water) is open, with what's chosen in it,
   * and the bridging assistant's choices for each step (by rank index). `helper`: the scroll was picked in the
   * town's enchanting helper (it draws the pick, not the dialog), for that gear's slot. `swap`: the bridging rank
   * (its index) whose list of scrolls to swap in is open.
   */
  enchant: ({
    mode: 'enchant' | 'anoint'; itemUid: string; targetUid?: string; bridge?: Record<number, EnchantChoice>;
    helper?: { gearUid: string; slot: 'prefix' | 'suffix' }; swap?: number;
  } & EnchantChoice) | null;
  /** The town's enchanting helper (client/enchant-helper.ts): open, and the gear and slot picked in it. The scroll picked is `enchant` with `helper` set. */
  enchantHelper: { gearUid?: string; slot?: 'prefix' | 'suffix' } | null;
  /** The powder you picked for each enchanting request you were asked (by request id), when it's left to you. */
  enchantAnswer: Record<string, string>;
  /**
   * Ground items (by uid) whose loot panel you collapsed: while everything within reach is among them, the panel
   * stays a one-line bar; something new within reach opens it again. Cleared in town.
   */
  lootDismissed: Set<string>;
  /** The square clicked to open the loot panel, listed first and outlined brighter (cleared when it leaves your reach). */
  pickupFocus: { x: number; y: number } | null;
  /** Walking to items: take the item, or point the loot panel at them, on arrival. */
  pickupAt: { x: number; y: number } | null;
  /** Walking to a door you know is trapped: Open Door on it once you're next to it (walking into it is refused). */
  openDoorAt: { x: number; y: number } | null;
  /** The stack in your gear whose Drop chooser (1, a typed amount, or all) is open, and the amount typed into it. */
  dropOpen: string | null;
  dropTyped: string;
  /** The Sell button waiting for a yes: the item and which sale (town, or to a merchant or haven trader). Shift+click skips it. */
  sellConfirm: { uid: string; how: 'town' | 'trade' } | null;
  /** You're spending your construct companion's actions (map clicks, keys and the action bar act for it). */
  controlling: boolean;
  /** Who you chose to talk to (a chat target value, see talk.ts); null: whoever's natural (an NPC in earshot, else the party). */
  talkTo: string | null;
  /** The Recall Knowledge question the chat box last switched to by itself (a new one takes it again). */
  talkRecall: string | null;
  /** Your pick before that question took the chat box (it comes back when the question closes). */
  talkBeforeRecall: string | null;
  /** The town shop's open section ('all' lists every section under its own heading, the Enchanter's Emporium last). */
  shopTab: 'all' | 'emporium' | ShopCategory;
  /** The level-up pick modal's search and filters (they restart with each pick). */
  pickUi: { handId: string; list: ListState };
  /** The attribute boost picker: open, the attributes chosen in it, and put off until later. */
  boost: { open: boolean; chosen: Attribute[]; later: boolean };
  /** The town's Retrain panel: the choice picked to swap, the server's options for it, and the list's filters. */
  retrain: { kind: 'feat' | 'skill-increase' | 'spell'; remove: string } | null;
  retrainOptions: { kind: 'feat' | 'skill-increase' | 'spell'; remove: string; options: { id: string; eligible: boolean; reason?: string }[] } | null;
  retrainList: ListState;
  /** The town's theme box as typed but not yet sent (null: show the theme the server has). */
  themeDraft: string | null;
}

export const store: Store = {
  playerId: null,
  welcome: null,
  joined: false,
  view: null,
  targeting: null,
  hover: null,
  walkTo: null,
  modalDismissedFor: null,
  showLogin: false,
  campOpen: false,
  transfer: null,
  journalOpen: false,
  bag: null,
  enchant: null,
  enchantHelper: null,
  enchantAnswer: {},
  lootDismissed: new Set(),
  pickupFocus: null,
  pickupAt: null,
  openDoorAt: null,
  dropOpen: null,
  dropTyped: '',
  sellConfirm: null,
  controlling: false,
  talkTo: null,
  talkRecall: null,
  talkBeforeRecall: null,
  shopTab: 'all',
  pickUi: { handId: '', list: newListState() },
  boost: { open: false, chosen: [], later: false },
  retrain: null,
  retrainOptions: null,
  retrainList: newListState(),
  themeDraft: null,
};

export function me(view: PlayerView | null) {
  return view?.dungeon?.creatures.find((c) => c.isYou) ?? null;
}

/** The creature your map clicks and keys act for: your construct while you control it, otherwise you. */
export function actorOf(view: PlayerView | null) {
  if (store.controlling && view?.construct) return view.dungeon?.creatures.find((c) => c.id === view.construct!.id) ?? null;
  return me(view);
}

/** The actions the action bar and hotkeys offer: your construct's while you control it. */
export function activeActions(view: PlayerView): ActionView[] {
  return store.controlling && view.construct ? view.construct.actions : view.actions;
}

/** Your construct's id while you control it (sent as the actor of each action). */
export function controlledId(view: PlayerView | null): string | undefined {
  return store.controlling && view?.construct ? view.construct.id : undefined;
}
